import React, { useState } from 'react';
import { X, Star, Filter, Share2, Linkedin, Link2, ChevronLeft, Building2 } from 'lucide-react';
import type { PublicVendorReview } from '@clareefai/types';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';

interface ReviewsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reviews: PublicVendorReview[];
  companyName: string;
  pageSlug?: string;
  onRequestCall?: (reviewId: string, promoterId: string, promoterName: string) => void;
}

const StarRating: React.FC<{ rating: number; className?: string }> = ({
  rating,
  className = 'w-5 h-5',
}) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${className} ${
          i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'
        }`}
      />
    ))}
  </div>
);

const Initials: React.FC<{ name: string; className?: string }> = ({
  name,
  className = 'w-10 h-10 text-sm',
}) => (
  <div
    className={`${className} bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
  >
    {name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()}
  </div>
);

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const INDUSTRY_OPTIONS = ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing'];

const ReviewsDialog: React.FC<ReviewsDialogProps> = ({
  isOpen,
  onClose,
  reviews,
  companyName,
  pageSlug,
  onRequestCall,
}) => {
  const [selectedReview, setSelectedReview] = useState<PublicVendorReview | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [filterCompanySize, setFilterCompanySize] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  if (!isOpen) return null;

  const filteredReviews = reviews;

  const handleShareLinkedIn = (review: PublicVendorReview) => {
    const url = pageSlug
      ? `${PUBLIC_SITE_URL}/company/${pageSlug}`
      : window.location.href;
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
  };

  const handleCopyLink = () => {
    const url = pageSlug
      ? `${PUBLIC_SITE_URL}/company/${pageSlug}`
      : window.location.href;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {selectedReview ? (
            <button
              onClick={() => setSelectedReview(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-2xl font-bold text-gray-900">Back to All Reviews</span>
            </button>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">All Customer Reviews</h2>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        {!selectedReview && (
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="relative">
              <select
                value={filterCompanySize}
                onChange={(e) => setFilterCompanySize(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Company Size</option>
                {COMPANY_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={filterIndustry}
                onChange={(e) => setFilterIndustry(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Industry</option>
                {INDUSTRY_OPTIONS.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="appearance-none pl-9 pr-8 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300 transition-colors cursor-pointer"
              >
                <option value="">Product</option>
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
        )}

        {/* Detail view */}
        {selectedReview ? (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              <StarRating rating={selectedReview.rating || 0} className="w-6 h-6" />
              <blockquote className="text-lg text-gray-800 mt-4 mb-6 leading-relaxed">
                "{selectedReview.comment}"
              </blockquote>
              <div className="flex items-center gap-4 mb-6">
                <Initials name={selectedReview.promoterName} className="w-12 h-12 text-base" />
                <div>
                  <p className="font-semibold text-gray-900">{selectedReview.promoterName}</p>
                  {selectedReview.promoterCompany && (
                    <p className="text-sm text-gray-500">{selectedReview.promoterCompany}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleShareLinkedIn(selectedReview)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white rounded-lg text-sm font-medium hover:bg-[#004182] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  Share on LinkedIn
                </button>
                <button
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Link2 className="w-4 h-4" />
                  {copiedLink ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Grid view */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReviews.map((review) => (
              <div
                key={review.id}
                onClick={() => setSelectedReview(review)}
                className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-md hover:border-blue-100 transition-all cursor-pointer"
              >
                <StarRating rating={review.rating || 0} />
                <p className="text-gray-700 mt-3 mb-4 line-clamp-4 leading-relaxed">
                  "{review.comment}"
                </p>
                <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
                  <span>Review from</span>
                  <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-[8px] font-bold">C</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Initials name={review.promoterName} className="w-9 h-9 text-xs" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{review.promoterName}</p>
                    {review.promoterCompany && (
                      <p className="text-xs text-gray-500 truncate">{review.promoterCompany}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsDialog;
